/**
 * Counter — travel through the chain's steps (board `ColumnFurniture`, frames 2–4; alternative C
 * of the `HistoryTravel` board, chosen 2026-08-31). `‹ 3/5 ›` in mono between the picture and the
 * bar, and the travel pair itself is there only above one step: nothing to travel, nothing to
 * travel with (frame 1). Its width is the same at two steps or at two hundred, which is why it
 * replaced the dots.
 *
 * A chain rooted on a wall tile begins AT that tile (`chains.chainSteps`): step 1 is the basis, and
 * the takes follow. So a tile twisted once reads `‹ 2/2 ›` — the picture that was twisted, then the
 * twist.
 *
 * The number pair is display, not a control — only `‹ ›` step. Stepping changes nothing else about
 * the column: the step on screen is still the one the next Generate continues from (BRIEF
 * § Regenerate) and the frame still re-fits to its ratio.
 *
 * A take that is queued or rendering has no picture yet; the dots said so by going dashed, and the
 * counter says it in the same ink — the current number unlit, over a dashed accent rule. The basis
 * is a picture that already exists, so it is never unlit.
 *
 * The pin door (plan `2026_09_04_wall_pin.md`, BRIEF Iteration II §4, board `PinDoor` option 3): one
 * glyph AFTER THE ARROWS, a bookmark outline in the counter's own ink, filled in the accent when
 * pinned — no word in the row, hover/long-press says `pin`/`unpin`, toggles. The plan's own words
 * place it "beside the counter," and every drawn frame (board `Pin`, `PinDoor`) shows it on a chain
 * already carrying the travel pair — so the door shares the counter's OWN gate (`total < 2` renders
 * neither): a chain of exactly one take has no counter to stand beside, and the door does not
 * appear alone (verified live 2026-09-04 — a bare bookmark under a single-take result read as
 * orphaned chrome with nothing to its left). `pin` may still be non-null on such a chain (`Wall.tsx`
 * gates it only on "a done take is on screen"); this component is what withholds it until a second
 * take exists.
 *
 * `layout` (plan `2026_09_18_armed_frame_budget.md` § D3, superseded on the desktop column after
 * the dev look, 2026-09-18 — see `thinking/threads/general_generator.md`): D3 stood the whole
 * counter beside the frame as one block, which on dev wrapped `12 / 12` one token per line (the
 * block had no width to hold it) and floated surfaceless on the dimmed wall. `layout='split'`
 * answers by drawing the counter's two natures separately — the travel pair is a CONTROL, the
 * readout is a DISPLAY (this file's own header, above): the arrows flank the frame as free-standing
 * buttons, vertically centred on it, and the readout keeps its pin door but moves into a small
 * surfaced pill inside the frame's own corner, so it reads as belonging to the picture rather than
 * floating beside it. `layout='row'` is the shape every caller had before this decision (the
 * Sheet's own single line, and the Column's narrow-viewport fallback) — the default, so neither
 * changes by adding this prop.
 *
 * Second dev pass, same day (operator-tested on dev, Safari macOS and a phone,
 * `thinking/threads/general_generator.md`): `layout='split'`'s own arrows stood 44x44 islands 14px
 * off the frame, so a near miss on a rapid ‹ ‹ ‹ run fell on the scrim between them
 * (`Column.tsx`'s own `onClick={onClose}`) and read as the column closing; at either end of the
 * chain `disabled` made it worse — a disabled button swallows its OWN click rather than the scrim's,
 * so the last press of the run landed on nothing at all. Every arrow — in `layout='split'` and in
 * `layout='row'` alike — is now a real click target across the whole chain: `layout='split'`'s
 * arrows are FLANK ZONES spanning the frame's full height and touching it directly
 * (`Counter.module.scss`'s `.arrowLeft`/`.arrowRight`, `frame.ts`'s `COUNTER_FLANK_W`), and an end of
 * the chain carries `aria-disabled` rather than `disabled` — the press is absorbed by the zone's own
 * no-op handler instead of falling through to whatever stands behind it. `layout='row'`'s own arrows
 * (the Sheet, and the Column's narrow-viewport fallback) hold still now too: the row used to be one
 * inline group — arrows, readout, pin door — that re-centred under the thumb whenever the pin door
 * came and went (a done take carries one, the basis and a queued/rendering take never did). A fixed,
 * centred travel group and a pin slot standing OUTSIDE it (`Counter.module.scss`'s `.travel`/
 * `.pinSlot`) replace it, so neither the pin's presence nor its state ever shifts the arrows under a
 * moving thumb (see this file's own `PinSlotState`, below).
 *
 * `layout='inside'` (2026-09-18, `thinking/threads/general_generator.md`): the Sheet's own in-flow
 * `layout='row'` counter (44px, since the reach fix the same day) made the sheet ~90% of an iPhone
 * SE's height. There is no room BESIDE the picture on the phone the way `.frameWrap` gives the
 * column, so the zones go INSIDE it instead — same Fragment as 'split', same handlers, same
 * `aria-disabled` end-of-chain rule, same pill; only the zone classes differ
 * (`Counter.module.scss`'s `.zoneLeft`/`.zoneRight`) and the zones drop `data-wall-return-exempt` —
 * see this prop's own doc, below, for why.
 *
 * One component for the column and the sheet: the board draws the counter identically in frames 2,
 * 3 and 4, so there is one build of it.
 */
import {chainTravel, type ChainStep} from './chains'
import {DEFAULT_CHROME, type WallChrome} from './theme'
import styles from './Counter.module.scss'

export type PinDoorProps = {
    pinned: boolean
    onToggle: () => void
}

/**
 * The pin slot's own state (second dev pass, 2026-09-18): `'live'` — a done take is on screen, `pin`
 * carries its toggle, drawn exactly as before. `'idle'` — the step on screen cannot be pinned (the
 * basis, or a queued/rendering take: `pinnedAt` is a stamp on a generation row, and neither one is
 * one — plan `2026_09_04_wall_pin.md`, "a pin can only ever be a picture that exists") but the reader
 * is signed in, so the same bookmark stands outline-only and inert rather than vanishing — the split
 * pill's own width would otherwise change across every step of a chain, and the row layout's pin slot
 * would otherwise appear and disappear under a moving thumb. `'none'` — signed out, or no counter to
 * stand beside at all; nothing renders, as before. Left `undefined`, the slot infers `'live'` from a
 * non-null `pin` and `'none'` otherwise — every caller from before this pass keeps its exact shape.
 */
export type PinSlotState = 'live' | 'idle' | 'none'

export type CounterProps = {
    /** This chain's steps, oldest first (`chains.chainSteps`). */
    steps: readonly ChainStep[]
    /** The one on screen; `null` = the newest, which is where an arm opens. */
    selectedStepId: string | null
    onSelectStep: (id: string) => void
    /** The pin door's own live state — read only while `pinSlot` is `'live'` (or omitted and a
     *  non-null value here implies it, see `PinSlotState`'s own doc). The caller (`Wall.tsx`) still
     *  gates this on the displayed step being a signed-in user's own DONE take. */
    pin?: PinDoorProps | null
    /** Which of the pin slot's three states to draw — see `PinSlotState`'s own doc. This component
     *  further withholds the whole counter (pin included) below two steps, where it does not stand
     *  at all (see this file's header doc). */
    pinSlot?: PinSlotState
    /** 'row' (default, unchanged): the arrows, the readout and the pin door in one line — the
     *  Column's own narrow-viewport fallback (the Sheet no longer uses this shape, 2026-09-18).
     *  'split': three siblings (prev arrow, the readout+pin pill, next arrow) for the caller's
     *  positioned wrapper (`Column.tsx`'s `.frameWrap`) to place on its own — see this file's header
     *  doc. 'inside': the SAME three siblings, for a caller whose positioned wrapper IS the picture
     *  itself (`Sheet.tsx`'s `.result`, no room beside it on the phone) — `Counter.module.scss`'s
     *  `.zoneLeft`/`.zoneRight` instead of `.arrowLeft`/`.arrowRight`, and the two zones carry no
     *  `data-wall-return-exempt`: a tap on one has no travel to exempt from the wall's own return
     *  gesture, but a downward drag that starts on the picture's own side must still fold the sheet
     *  (`Wall.tsx`'s return listener excludes only exempt touches). The pill keeps the attribute,
     *  same as 'split'. */
    layout?: 'row' | 'split' | 'inside'
    /** The bookmark door's titles — the house's own (`chromeWords(theme).pin`). */
    pinWords?: WallChrome['pin']
}

const PIN_PATH = 'M4 2.5h8v11l-4-3-4 3z' // the bookmark outline, board `PinDoor` option 3

/**
 * The pin door in whichever of its two drawable states applies (`'none'` draws nothing) —
 * `extraClass` is how the two layouts give it their own box model: the split pill's own hit-area
 * trick (`.door`'s own padding/negative-margin, unchanged) for `layout='split'`, a plain 44x44 flex
 * box (`.doorRow`) for `layout='row'`'s own standing pin slot, which the row's own fixed structure
 * (this file's header doc) no longer needs to hide inside a shared inline line-height.
 */
function pinDoor(pinSlot: PinSlotState, pin: PinDoorProps | null, extraClass: string, words: WallChrome['pin']) {
    if (pinSlot === 'live' && pin) {
        return (
            <button
                type="button"
                className={[styles.door, extraClass, pin.pinned ? styles.doorOn : ''].filter(Boolean).join(' ')}
                onClick={pin.onToggle}
                title={pin.pinned ? words.on : words.off}
                aria-label={pin.pinned ? words.on : words.off}
                aria-pressed={pin.pinned}
            >
                <svg
                    viewBox="0 0 16 16" width="13" height="13" aria-hidden="true"
                    fill={pin.pinned ? 'currentColor' : 'none'} stroke="currentColor"
                    strokeWidth="1.4" strokeLinejoin="round"
                >
                    <path d={PIN_PATH} />
                </svg>
            </button>
        )
    }
    if (pinSlot === 'idle') {
        return (
            <button
                type="button"
                className={[styles.door, extraClass, styles.doorIdle].filter(Boolean).join(' ')}
                aria-disabled="true"
                title={words.idle}
                aria-label={words.idle}
            >
                <svg
                    viewBox="0 0 16 16" width="13" height="13" aria-hidden="true"
                    fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"
                >
                    <path d={PIN_PATH} />
                </svg>
            </button>
        )
    }
    return null
}

export function Counter({steps, selectedStepId, onSelectStep, pin = null, pinSlot, layout = 'row', pinWords = DEFAULT_CHROME.pin}: CounterProps) {
    const {index, total, step, prev, next} = chainTravel(steps, selectedStepId)
    if (total < 2 || !step) return null
    const working = step.kind === 'take' && step.take.status !== 'done'
    const resolvedPinSlot: PinSlotState = pinSlot ?? (pin ? 'live' : 'none')

    const readout = (
        <span className={styles.readout} role="status" aria-label={`Step ${index + 1} of ${total}`}>
            <span className={working ? styles.nowWorking : styles.now}>{index + 1}</span>
            <span className={styles.of}>{' / '}{total}</span>
        </span>
    )

    if (layout === 'split' || layout === 'inside') {
        // 'inside' is 'split' drawn over the picture itself rather than beside it: same handlers,
        // same end-of-chain rule, same pill — only the zone classes and the zones' own return-exempt
        // marking differ (this file's header doc states why the zones drop it).
        const inside = layout === 'inside'
        return (
            <>
                <button
                    type="button"
                    className={inside ? styles.zoneLeft : styles.arrowLeft}
                    onClick={() => prev && onSelectStep(prev.id)}
                    aria-disabled={prev ? undefined : 'true'}
                    aria-label="Previous step"
                    data-wall-return-exempt={inside ? undefined : 'true'}
                >
                    ‹
                </button>
                <div className={inside ? `${styles.pill} ${styles.pillInside}` : styles.pill} data-wall-return-exempt="true">
                    {readout}
                    {pinDoor(resolvedPinSlot, pin, '', pinWords)}
                </div>
                <button
                    type="button"
                    className={inside ? styles.zoneRight : styles.arrowRight}
                    onClick={() => next && onSelectStep(next.id)}
                    aria-disabled={next ? undefined : 'true'}
                    aria-label="Next step"
                    data-wall-return-exempt={inside ? undefined : 'true'}
                >
                    ›
                </button>
            </>
        )
    }

    return (
        <div className={styles.count} data-wall-return-exempt="true">
            <div className={styles.travel}>
                <button
                    type="button"
                    className={styles.arw}
                    onClick={() => prev && onSelectStep(prev.id)}
                    aria-disabled={prev ? undefined : 'true'}
                    aria-label="Previous step"
                >
                    ‹
                </button>
                {readout}
                <button
                    type="button"
                    className={styles.arw}
                    onClick={() => next && onSelectStep(next.id)}
                    aria-disabled={next ? undefined : 'true'}
                    aria-label="Next step"
                >
                    ›
                </button>
            </div>
            {resolvedPinSlot !== 'none' && (
                <div className={styles.pinSlot}>{pinDoor(resolvedPinSlot, pin, styles.doorRow, pinWords)}</div>
            )}
        </div>
    )
}
